/**
 * PayPal Subscription Integration - Replaces Stripe Checkout
 * Handles:
 * - Plan selection for unauthenticated users (with selectedPlan storage)
 * - Dynamic button states based on authentication and current plan
 * - Authenticated subscription flow with userId
 * - Post-login plan continuation
 */

function getPayPalClientId() {
    return window.PAYPAL_CLIENT_ID || 'YOUR_PAYPAL_CLIENT_ID';
}

function getPayPalPlanIds() {
    return window.PAYPAL_PLAN_IDS || {
        pro: 'P-REPLACE_PRO',
        unlimited: 'P-REPLACE_UNLIMITED'
    };
}

function getPayPalPromoPlanIds() {
    return window.PAYPAL_PROMO_PLAN_IDS || {};
}

function getPayPalPromoCode() {
    return window.PAYPAL_PROMO_CODE || 'TFX20';
}

function getPayPalCurrency() {
    return window.PAYPAL_CURRENCY || 'EUR';
}

function getPayPalEnv() {
    return window.PAYPAL_ENV || 'live';
}

let paypalSdkPromise = null;
let isProcessingCheckout = false;
let currentRenderedPlan = null;
let paymentMethodPlan = null;
let promoApplied = false;
let currentCheckoutPlan = null;
let currentDiscountData = null;
let isProcessingPromo = false;  // Prevenir ejecuciones simultáneas
let planPrices = { pro: 9.99, unlimited: 14.99, free: 0 };

function loadPayPalSdk() {
    if (paypalSdkPromise) return paypalSdkPromise;

    paypalSdkPromise = new Promise((resolve, reject) => {
        if (window.paypal) {
            resolve(window.paypal);
            return;
        }

        const clientId = getPayPalClientId();
        if (!clientId || clientId === 'YOUR_PAYPAL_CLIENT_ID') {
            reject(new Error('PAYPAL_CLIENT_ID no configurado'));
            return;
        }

        const script = document.createElement('script');
        const env = getPayPalEnv();
        const currency = getPayPalCurrency();
        const base = env === 'sandbox' ? 'https://www.sandbox.paypal.com/sdk/js' : 'https://www.paypal.com/sdk/js';
        script.src = `${base}?client-id=${encodeURIComponent(clientId)}&vault=true&intent=subscription&currency=${encodeURIComponent(currency)}`;
        script.async = true;
        script.onload = () => resolve(window.paypal);
        script.onerror = () => reject(new Error('No se pudo cargar el SDK de PayPal'));
        document.head.appendChild(script);
    });

    return paypalSdkPromise;
}

function ensurePayPalModal() {
    let modal = document.getElementById('paypal-modal');
    if (modal) {
        // SIEMPRE re-añadir listener del botón "Aplicar", puede haberse perdido
        const applyBtn = modal.querySelector('#paypal-promo-apply');
        if (applyBtn) {
            // Eliminar listeners viejos clonando el nodo
            const newBtn = applyBtn.cloneNode(true);
            applyBtn.parentNode.replaceChild(newBtn, applyBtn);
            newBtn.addEventListener('click', applyPayPalPromoCode);
        }
        return modal;
    }

    modal = document.createElement('div');
    modal.id = 'paypal-modal';
    modal.style.cssText = `
        position: fixed;
        inset: 0;
        display: none;
        align-items: center;
        justify-content: center;
        background: rgba(0,0,0,0.6);
        z-index: 10000;
        padding: 24px;
    `;

    modal.innerHTML = `
        <div style="width: min(420px, 100%); background: #0f172a; border-radius: 16px; border: 1px solid rgba(148,163,184,0.2); box-shadow: 0 24px 80px rgba(0,0,0,0.6);">
            <div style="display:flex; align-items:center; justify-content:space-between; padding:16px 18px; border-bottom:1px solid rgba(148,163,184,0.15);">
                <div style="color:#e2e8f0; font-weight:700;" id="paypal-modal-title">Suscripción PayPal</div>
                <button id="paypal-modal-close" style="background:transparent; color:#94a3b8; border:none; font-size:18px; cursor:pointer;">✕</button>
            </div>
            <div style="padding: 18px; color:#cbd5f5; font-size: 13px;">
                <div id="paypal-modal-message" style="margin-bottom: 12px; color:#94a3b8;">Completa el pago seguro con PayPal.</div>
                <div style="display:flex; gap:8px; margin-bottom:12px;">
                    <input id="paypal-promo-code" type="text" placeholder="Código de descuento" style="flex:1; padding:10px 12px; border-radius:10px; border:1px solid rgba(148,163,184,0.2); background:#0b1220; color:#e2e8f0;" />
                    <button id="paypal-promo-apply" type="button" style="padding:10px 12px; border-radius:10px; border:1px solid rgba(148,163,184,0.2); background:#1e293b; color:#e2e8f0; font-weight:600; cursor:pointer;">Aplicar</button>
                </div>
                <div id="paypal-promo-feedback" style="margin-bottom:12px; font-size:11px; color:#94a3b8;"></div>
                <div id="paypal-button-container"></div>
            </div>
        </div>
    `;

    document.body.appendChild(modal);
    modal.querySelector('#paypal-modal-close').addEventListener('click', closePayPalModal);
    modal.addEventListener('click', (event) => {
        if (event.target === modal) closePayPalModal();
    });

    const applyBtn = modal.querySelector('#paypal-promo-apply');
    const promoInput = modal.querySelector('#paypal-promo-code');
    if (applyBtn) {
        applyBtn.addEventListener('click', applyPayPalPromoCode);
    }
    if (promoInput) {
        promoInput.addEventListener('keydown', (event) => {
            if (event.key === 'Enter') {
                event.preventDefault();
                applyPayPalPromoCode();
            }
        });
    }

    // Agregar delegación de eventos en body como fallback
    document.body.addEventListener('click', (event) => {
        if (event.target?.id === 'paypal-promo-apply') {
            applyPayPalPromoCode();
        }
    });

    return modal;
}

function openPayPalModal(planName) {
    console.log('[MODAL] openPayPalModal llamada con planName:', planName);
    const modal = ensurePayPalModal();
    const title = modal.querySelector('#paypal-modal-title');
    const message = modal.querySelector('#paypal-modal-message');
    const promoInput = modal.querySelector('#paypal-promo-code');
    const promoFeedback = modal.querySelector('#paypal-promo-feedback');
    
    if (title) title.textContent = `Suscripción ${planName.toUpperCase()}`;
    
    const originalPrice = planPrices[planName] || 0;
    if (message) {
        message.textContent = `Precio: €${originalPrice.toFixed(2)}/mes • Ingresa código de descuento abajo`;
    }
    
    promoApplied = false;
    currentCheckoutPlan = planName;
    sessionStorage.setItem('checkoutPlan', planName); // Guardar en sessionStorage
    currentDiscountData = null;
    
    console.log('[MODAL] currentCheckoutPlan seteado a:', currentCheckoutPlan);
    
    if (promoInput) promoInput.value = '';
    if (promoFeedback) promoFeedback.textContent = '';
    
    modal.style.display = 'flex';
    console.log('[MODAL] Modal mostrado');
}

function setPayPalPromoFeedback(text, isError = false) {
    const modal = ensurePayPalModal();
    const feedback = modal.querySelector('#paypal-promo-feedback');
    if (feedback) {
        feedback.textContent = text;
        feedback.style.color = isError ? '#fca5a5' : '#86efac';
    }
}

async function applyPayPalPromoCode() {
    // Prevenir ejecuciones simultáneas
    if (isProcessingPromo) {
        console.log('[PROMO] Ya se está procesando un código, ignorando...');
        return;
    }
    
    isProcessingPromo = true;
    
    try {
        const modal = ensurePayPalModal();
        const promoInput = modal.querySelector('#paypal-promo-code');
        const code = (promoInput?.value || '').trim().toUpperCase();

        // Extraer plan del título del modal si es necesario
        const titleEl = document.getElementById('paypal-modal-title');
        const titleText = titleEl 
            ? titleEl.textContent.toLowerCase().replace('suscripción ', '').trim() 
            : null;
        let plan = currentCheckoutPlan || sessionStorage.getItem('checkoutPlan') || titleText;
    
        console.log('[PROMO] currentCheckoutPlan:', currentCheckoutPlan, 'sessionStorage.checkoutPlan:', sessionStorage.getItem('checkoutPlan'), 'final plan:', plan, 'code:', code);

        if (!plan) {
            console.log('[PROMO] Sin plan seleccionado');
            setPayPalPromoFeedback('Selecciona un plan primero.', true);
            isProcessingPromo = false;
            return;
        }

        if (!code) {
            promoApplied = false;
            currentDiscountData = null;
            setPayPalPromoFeedback('Introduce un código válido.', true);
            await renderPayPalButtons(plan);
            isProcessingPromo = false;
            return;
        }

        // Validar código de descuento contra API
        const baseUrl = window.WORKER_URL || 'https://trading-journal-preprod.monkeydfx-trader.workers.dev';
        console.log('[PROMO] Enviando:', { code, planName: plan });
        const response = await fetch(`${baseUrl}/api/validate-discount-code`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ code, planName: plan })
        });

        const data = await response.json();
        console.log('[PROMO] Respuesta:', data);

        if (data.valid) {
            promoApplied = true;
            currentDiscountData = data;
            const finalPrice = data.finalPrice || 0;
            const originalPrice = data.originalPrice || planPrices[plan] || 0;
            const discountPct = data.discountPercentage || 0;
            
            if (finalPrice === 0) {
                setPayPalPromoFeedback(`✅ Descuento 100% aplicado. Total: $0 (gratis)`, false);
            } else {
                setPayPalPromoFeedback(`✅ Descuento ${discountPct}% aplicado. Total: €${finalPrice.toFixed(2)}`, false);
            }
            
            // Si el precio final es 0, preparar transacción gratuita
            if (finalPrice === 0) {
                await renderFreeCheckoutOption(plan, data);
            } else {
                await renderPayPalButtons(plan);
            }
        } else {
            promoApplied = false;
            currentDiscountData = null;
            setPayPalPromoFeedback(`❌ ${data.error || 'Código no válido'}`, true);
            await renderPayPalButtons(plan);
        }
    } catch (error) {
        console.error('[Error al validar código]:', error);
        promoApplied = false;
        currentDiscountData = null;
        setPayPalPromoFeedback(`❌ Error al validar código: ${error.message}`, true);
        
        // Intentar obtener el plan para renderizar botones
        const modal = document.getElementById('paypal-modal');
        if (modal) {
            const titleEl = document.getElementById('paypal-modal-title');
            const plan = (titleEl?.textContent || '').toLowerCase().replace('suscripción ', '').trim();
            if (plan) await renderPayPalButtons(plan);
        }
    } finally {
        isProcessingPromo = false;
    }
}

function setPayPalModalMessage(text, isError = false) {
    const modal = ensurePayPalModal();
    const message = modal.querySelector('#paypal-modal-message');
    if (message) {
        message.textContent = text;
        message.style.color = isError ? '#fca5a5' : '#94a3b8';
    }
}

function closePayPalModal() {
    const modal = document.getElementById('paypal-modal');
    if (modal) modal.style.display = 'none';
}

function isLemonAvailable(planName) {
    const urls = window.LEMON_CHECKOUT_URLS || {};
    return !!urls[planName];
}

function ensurePaymentMethodModal() {
    let modal = document.getElementById('payment-method-modal');
    if (modal) return modal;

    modal = document.createElement('div');
    modal.id = 'payment-method-modal';
    modal.style.cssText = `
        position: fixed;
        inset: 0;
        display: none;
        align-items: center;
        justify-content: center;
        background: rgba(0,0,0,0.6);
        z-index: 10000;
        padding: 24px;
    `;

    modal.innerHTML = `
        <div style="width: min(420px, 100%); background: #0f172a; border-radius: 16px; border: 1px solid rgba(148,163,184,0.2); box-shadow: 0 24px 80px rgba(0,0,0,0.6);">
            <div style="display:flex; align-items:center; justify-content:space-between; padding:16px 18px; border-bottom:1px solid rgba(148,163,184,0.15);">
                <div style="color:#e2e8f0; font-weight:700;">Elige método de pago</div>
                <button id="payment-method-close" style="background:transparent; color:#94a3b8; border:none; font-size:18px; cursor:pointer;">✕</button>
            </div>
            <div style="padding: 18px; color:#cbd5f5; font-size: 13px; display: grid; gap: 12px;">
                <button id="payment-method-paypal" type="button" style="width:100%; padding:12px 14px; border-radius:10px; border:1px solid rgba(59,130,246,0.35); background:linear-gradient(135deg,#1e3a8a,#1e293b); color:#e2e8f0; font-weight:600; cursor:pointer; display:flex; align-items:center; justify-content:center; gap:10px;">
                    <span style="display:inline-flex; align-items:center; justify-content:center; width:20px; height:20px;">
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true" xmlns="http://www.w3.org/2000/svg">
                            <path d="M7.5 20.5H5.8a.8.8 0 0 1-.79-.96l2.7-15.3A2.2 2.2 0 0 1 9.87 2.5h5.45c3.18 0 5.05 1.93 4.53 4.78-.47 2.6-2.43 4.07-5.13 4.07h-2.3a.9.9 0 0 0-.89.75l-.86 5.1a.9.9 0 0 1-.89.75H7.5Z" fill="#2563EB"/>
                            <path d="M10.2 20.5h-1.5a.8.8 0 0 1-.79-.96l.2-1.2h1.64a2.2 2.2 0 0 0 2.17-1.83l.08-.5.86-5.1a2.2 2.2 0 0 1 2.17-1.83h2.1c.64 0 1.25-.1 1.82-.3-.6 3.5-3.2 6.12-6.96 6.12h-.8a.9.9 0 0 0-.89.75l-.38 2.25a.9.9 0 0 1-.89.75Z" fill="#60A5FA"/>
                        </svg>
                    </span>
                    <span>Pagar con PayPal</span>
                </button>
            </div>
        </div>
    `;

    document.body.appendChild(modal);
    modal.querySelector('#payment-method-close').addEventListener('click', () => {
        modal.style.display = 'none';
    });
    modal.addEventListener('click', (event) => {
        if (event.target === modal) modal.style.display = 'none';
    });

    modal.querySelector('#payment-method-paypal').addEventListener('click', async () => {
        if (!paymentMethodPlan) return;
        modal.style.display = 'none';
        if (window.initiateCheckout) {
            await initiateCheckout(paymentMethodPlan);
        } else if (window.showToast) {
            window.showToast('PayPal no está configurado', 'error');
        }
    });


    return modal;
}

window.selectPaymentMethod = function(planName) {
    paymentMethodPlan = planName;
    const modal = ensurePaymentMethodModal();
    const cardBtn = modal.querySelector('#payment-method-card');
    const cardBadge = modal.querySelector('#payment-method-card-badge');
    if (cardBtn) {
        const enabled = isLemonAvailable(planName);
        cardBtn.style.display = 'block';
        cardBtn.disabled = false;
        if (enabled) {
            cardBtn.style.opacity = '1';
            cardBtn.style.cursor = 'pointer';
            cardBtn.style.background = '#0ea5e9';
            cardBtn.style.borderColor = 'rgba(148,163,184,0.2)';
            cardBtn.style.color = '#ffffff';
        } else {
            cardBtn.style.opacity = '0.55';
            cardBtn.style.cursor = 'not-allowed';
            cardBtn.style.background = '#334155';
            cardBtn.style.borderColor = 'rgba(148,163,184,0.35)';
            cardBtn.style.color = '#e2e8f0';
        }
        if (cardBadge) {
            cardBadge.style.display = enabled ? 'none' : 'inline-flex';
        }
    }
    modal.style.display = 'flex';
};

async function renderPayPalButtons(planName) {
    console.log('[RENDER] renderPayPalButtons llamada con planName:', planName);
    const basePlanIds = getPayPalPlanIds();
    const promoPlanIds = getPayPalPromoPlanIds();
    
    // Seleccionar plan: si hay descuento válido, buscar plan de promo correspondiente
    let selectedPlanId = basePlanIds[planName];
    
    if (currentDiscountData && currentDiscountData.discountPercentage > 0) {
        const pct = Math.round(currentDiscountData.discountPercentage);
        const promoKey = `${planName}_${pct}`;  // ej: "unlimited_50"
        
        if (promoPlanIds[promoKey]) {
            selectedPlanId = promoPlanIds[promoKey];
            console.log(`[PayPal] Usando plan de descuento: ${promoKey} → ${selectedPlanId}`);
        } else {
            console.warn(`[PayPal] Plan de promo no encontrado para: ${promoKey}. Usando plan base.`);
        }
    }
    
    if (!selectedPlanId) {
        console.error('[RENDER] selectedPlanId no encontrado para:', planName);
        setPayPalModalMessage('Plan de PayPal no configurado.', true);
        throw new Error('Plan de PayPal no configurado');
    }
    
    console.log('[RENDER] selectedPlanId:', selectedPlanId);

    // Asegurar que el contenedor existe
    let container = document.getElementById('paypal-button-container');
    if (!container) {
        console.error('[ERROR] Contenedor PayPal no encontrado, recreando modal...');
        ensurePayPalModal();
        container = document.getElementById('paypal-button-container');
    }
    
    if (!container) {
        throw new Error('No se pudo crear el contenedor PayPal');
    }

    const paypal = await loadPayPalSdk();

    container.innerHTML = '';
    currentRenderedPlan = planName;

    // Mostrar información de precio
    const originalPrice = planPrices[planName] || 0;
    const finalPrice = currentDiscountData?.finalPrice || originalPrice;
    const discountAmount = currentDiscountData?.discountAmount || 0;
    
    if (discountAmount > 0) {
        const priceInfo = document.createElement('div');
        priceInfo.style.cssText = `
            background: rgba(34,197,94,0.1);
            border: 1px solid rgba(34,197,94,0.3);
            border-radius: 8px;
            padding: 12px;
            margin-bottom: 12px;
            text-align: center;
            font-size: 12px;
        `;
        priceInfo.innerHTML = `
            <div style="color: #94a3b8; margin-bottom: 4px;">Precio original: <span style="text-decoration: line-through;">€${originalPrice.toFixed(2)}</span></div>
            <div style="color: #86efac; font-weight: 600;">Total: €${finalPrice.toFixed(2)}</div>
        `;
        container.appendChild(priceInfo);
    }

    const buttons = paypal.Buttons({
        createSubscription: (data, actions) => {
            return actions.subscription.create({
                plan_id: selectedPlanId,
                custom_id: currentDiscountData?.code || null
            });
        },
        onApprove: async (data) => {
            await handlePayPalApprove(data.subscriptionID, planName);
        },
        onCancel: () => {
            if (window.showToast) {
                window.showToast('Pago cancelado. No se realizaron cargos.', 'warning');
            }
            closePayPalModal();
        },
        onError: (err) => {
            console.error('[PayPal Error]:', err);
            if (window.showToast) {
                window.showToast('Error al iniciar PayPal. Intenta nuevamente.', 'error');
            }
            setPayPalModalMessage('Error al iniciar PayPal. Intenta nuevamente.', true);
        }
    });

    try {
        // Verificar que el contenedor aún existe antes de renderizar
        container = document.getElementById('paypal-button-container');
        if (!container) {
            throw new Error('Contenedor removido antes de renderizar');
        }
        
        await buttons.render(container);
        console.log('[PayPal] Botones renderizados exitosamente para:', planName);
    } catch (err) {
        console.error('[ERROR] Error al renderizar botones PayPal:', err);
        setPayPalModalMessage('Error al inicializar PayPal. Por favor recarga la página e intenta de nuevo.', true);
        throw err;
    }
}

async function renderFreeCheckoutOption(planName, discountData) {
    // Asegurar que el contenedor existe
    let container = document.getElementById('paypal-button-container');
    if (!container) {
        console.error('[ERROR] Contenedor PayPal no encontrado en renderFreeCheckoutOption, recreando modal...');
        ensurePayPalModal();
        container = document.getElementById('paypal-button-container');
    }
    
    if (!container) {
        throw new Error('No se pudo crear el contenedor PayPal');
    }

    container.innerHTML = `
        <div style="background: rgba(34,197,94,0.1); border: 1px solid rgba(34,197,94,0.3); border-radius: 8px; padding: 12px; margin-bottom: 12px; text-align: center;">
            <div style="color: #86efac; font-weight: 600; margin-bottom: 8px;">🎉 Descuento 100% - Suscripción Gratis</div>
            <div style="color: #cbd5f5; font-size: 12px; margin-bottom: 12px;">Se activará de inmediato sin cargos</div>
            <button id="free-checkout-btn" type="button" style="width: 100%; padding: 12px; border-radius: 8px; border: none; background: linear-gradient(135deg, #10b981, #059669); color: white; font-weight: 600; cursor: pointer; transition: all 0.3s;">Activar Suscripción Gratis ✅</button>
        </div>
    `;
    
    const btn = container.querySelector('#free-checkout-btn');
    if (btn) {
        btn.addEventListener('click', async () => {
            await handleFreeCheckout(planName, discountData);
        });
        btn.addEventListener('mouseover', () => {
            btn.style.transform = 'translateY(-2px)';
            btn.style.boxShadow = '0 10px 25px rgba(16,185,129,0.4)';
        });
        btn.addEventListener('mouseout', () => {
            btn.style.transform = 'none';
            btn.style.boxShadow = 'none';
        });
    }
}

async function handleFreeCheckout(planName, discountData) {
    const userToken = sessionStorage.getItem('userToken') || localStorage.getItem('userToken');
    const userId = sessionStorage.getItem('currentUserId') || localStorage.getItem('currentUserId');

    if (!userToken || !userId) {
        if (window.showToast) window.showToast('Sesión inválida. Inicia sesión de nuevo.', 'error');
        closePayPalModal();
        return;
    }

    const baseUrl = window.WORKER_URL || 'https://trading-journal-preprod.monkeydfx-trader.workers.dev';
    
    try {
        const btn = document.getElementById('free-checkout-btn');
        if (btn) btn.disabled = true;
        
        const response = await fetch(`${baseUrl}/api/free-checkout`, {
            method: 'POST',
            headers: {
                'Authorization': `Bearer ${userToken}`,
                'Content-Type': 'application/json'
            },
            body: JSON.stringify({
                planName,
                discountCode: discountData.code,
                userId
            })
        });

        if (!response.ok) {
            const error = await response.json().catch(() => ({}));
            if (window.showToast) {
                window.showToast(error.error || 'Error al procesar suscripción gratuita', 'error');
            }
            if (btn) btn.disabled = false;
            closePayPalModal();
            return;
        }

        const data = await response.json();
        
        localStorage.setItem('userPlan', planName);
        sessionStorage.setItem('userPlan', planName);

        if (window.syncUserDataFromDB) {
            await window.syncUserDataFromDB();
        }

        closePayPalModal();
        if (window.showToast) {
            window.showToast('✅ ¡Suscripción activada exitosamente!', 'success');
        }
        
        setTimeout(() => {
            window.location.href = `/payment-status?payment=success&plan=${planName}&free=true`;
        }, 1000);
    } catch (error) {
        if (window.showToast) {
            window.showToast('Error al activar la suscripción', 'error');
        }
        closePayPalModal();
    }
}

async function handlePayPalApprove(subscriptionId, planName) {
    const userToken = sessionStorage.getItem('userToken') || localStorage.getItem('userToken') ||
        sessionStorage.getItem('tfx_authToken') || localStorage.getItem('tfx_token');
    const userId = sessionStorage.getItem('currentUserId') || localStorage.getItem('currentUserId') ||
        sessionStorage.getItem('tfx_userId') || localStorage.getItem('tfx_userId');

    if (!userToken || !userId) {
        if (window.showToast) window.showToast('Sesión inválida. Inicia sesión de nuevo.', 'error');
        closePayPalModal();
        return;
    }

    const baseUrl = window.WORKER_URL || 'https://trading-journal-preprod.monkeydfx-trader.workers.dev';

    try {
        const response = await fetch(`${baseUrl}/api/paypal/verify`, {
            method: 'POST',
            headers: {
                'Authorization': `Bearer ${userToken}`,
                'Content-Type': 'application/json'
            },
            body: JSON.stringify({
                subscriptionId,
                userId,
                planName,
                discountCode: currentDiscountData?.code || null,
                discountPercentage: currentDiscountData?.discountPercentage || 0
            })
        });

        if (!response.ok) {
            const error = await response.json().catch(() => ({}));
            if (window.showToast) {
                window.showToast(error.error || 'No se pudo verificar la suscripción', 'error');
            }
            closePayPalModal();
            return;
        }

        const data = await response.json();
        const plan = data.plan || planName;

        localStorage.setItem('paypalSubscriptionId', subscriptionId);
        sessionStorage.setItem('paypalSubscriptionId', subscriptionId);
        localStorage.setItem('userPlan', plan);
        sessionStorage.setItem('userPlan', plan);

        // Limpiar descuento después de confirmar el pago
        currentDiscountData = null;
        promoApplied = false;

        if (window.syncUserDataFromDB) {
            await window.syncUserDataFromDB();
        }

        closePayPalModal();
        window.location.href = `/payment-status?payment=success&subscription_id=${encodeURIComponent(subscriptionId)}`;
    } catch (error) {
        if (window.showToast) {
            window.showToast('Error al activar la suscripción. Intenta de nuevo.', 'error');
        }
        closePayPalModal();
    }
}

// ============================================
// PLAN BUTTON MANAGEMENT
// ============================================

window.updatePlanButtons = function() {
    const userToken = sessionStorage.getItem('userToken') || localStorage.getItem('userToken');
    const userPlan = localStorage.getItem('userPlan') || 'free';

    const planFreeBtn = document.getElementById('freePlanBtn') ||
                        Array.from(document.querySelectorAll('button')).find(btn =>
                            btn.textContent.includes('Empezar Gratis'));

    const proPlanBtn = document.querySelector('.plan-pro button') ||
                       Array.from(document.querySelectorAll('button')).find(btn =>
                           btn.textContent.includes('Suscribirse Ahora') && btn.closest('.plan-pro'));

    const unlimitedBtn = document.querySelector('.plan-unlimited button') ||
                        Array.from(document.querySelectorAll('button')).find(btn =>
                           btn.textContent.includes('Suscribirse Ahora') && btn.closest('.plan-unlimited'));

    if (!userToken) {
        if (planFreeBtn) {
            planFreeBtn.textContent = 'EMPEZAR GRATIS';
            planFreeBtn.onclick = () => openRegistrationModal('free');
            planFreeBtn.style.opacity = '1';
            planFreeBtn.disabled = false;
            planFreeBtn.style.cursor = 'pointer';
        }

        if (proPlanBtn) {
            proPlanBtn.textContent = '🚀 Suscribirse';
            proPlanBtn.onclick = () => {
                if (window.showToast) {
                    window.showToast('📝 Debes crear una cuenta primero', 'info');
                }
                setTimeout(() => openRegistrationModal('pro'), 500);
            };
            proPlanBtn.style.opacity = '1';
            proPlanBtn.disabled = false;
            proPlanBtn.style.cursor = 'pointer';
        }

        if (unlimitedBtn) {
            unlimitedBtn.textContent = '⚡ Suscribirse';
            unlimitedBtn.onclick = () => {
                if (window.showToast) {
                    window.showToast('📝 Debes crear una cuenta primero', 'info');
                }
                setTimeout(() => openRegistrationModal('unlimited'), 500);
            };
            unlimitedBtn.style.opacity = '1';
            unlimitedBtn.disabled = false;
            unlimitedBtn.style.cursor = 'pointer';
        }
    } else {
        if (planFreeBtn) {
            planFreeBtn.style.display = 'none';
        }

        if (proPlanBtn) {
            if (userPlan === 'pro' || userPlan === 'unlimited') {
                proPlanBtn.textContent = '✅ Plan Actual';
                proPlanBtn.disabled = true;
                proPlanBtn.style.opacity = '0.5';
                proPlanBtn.style.cursor = 'not-allowed';
                proPlanBtn.onclick = null;
            } else {
                proPlanBtn.textContent = '🚀 Upgrade a PRO';
                proPlanBtn.disabled = false;
                proPlanBtn.style.opacity = '1';
                proPlanBtn.style.cursor = 'pointer';
                proPlanBtn.onclick = () => initiateCheckout('pro');
            }
        }

        if (unlimitedBtn) {
            if (userPlan === 'unlimited') {
                unlimitedBtn.textContent = '✅ Plan Actual';
                unlimitedBtn.disabled = true;
                unlimitedBtn.style.opacity = '0.5';
                unlimitedBtn.style.cursor = 'not-allowed';
                unlimitedBtn.onclick = null;
            } else {
                unlimitedBtn.textContent = '⚡ Upgrade a UNLIMITED';
                unlimitedBtn.disabled = false;
                unlimitedBtn.style.opacity = '1';
                unlimitedBtn.style.cursor = 'pointer';
                unlimitedBtn.onclick = () => initiateCheckout('unlimited');
            }
        }
    }
};

// ============================================
// AUTHENTICATED CHECKOUT
// ============================================

window.initiateCheckout = async function(planName) {
    console.log('[CHECKOUT] initiateCheckout llamada con:', planName);
    if (isProcessingCheckout) {
        console.log('[CHECKOUT] Ya está en proceso');
        return;
    }

    const userToken = sessionStorage.getItem('userToken') || localStorage.getItem('userToken');
    const userId = sessionStorage.getItem('currentUserId') || localStorage.getItem('userId') || localStorage.getItem('currentUserId');
    const userEmail = sessionStorage.getItem('userEmail') || localStorage.getItem('userEmail');
    const userPlan = localStorage.getItem('userPlan') || 'free';

    console.log('[CHECKOUT] Auth check:', { userToken: !!userToken, userId: !!userId, userEmail: !!userEmail, userPlan });

    if (!userToken || !userId || !userEmail) {
        if (window.showToast) {
            window.showToast('Por favor inicia sesión primero', 'error');
        }
        window.location.href = '/#planes';
        return;
    }

    if (userPlan === planName) {
        if (window.showToast) {
            window.showToast(`Ya tienes el plan ${planName.toUpperCase()}`, 'warning');
        }
        return;
    }

    const planIds = getPayPalPlanIds();
    if (!planIds[planName]) {
        if (window.showToast) {
            window.showToast('Plan no disponible en PayPal', 'error');
        }
        return;
    }

    const clientId = getPayPalClientId();
    if (!clientId || clientId === 'YOUR_PAYPAL_CLIENT_ID') {
        if (window.showToast) {
            window.showToast('PayPal no está configurado', 'error');
        }
        return;
    }

    isProcessingCheckout = true;
    showCheckoutLoader(true);

    try {
        openPayPalModal(planName);
        await renderPayPalButtons(planName);
    } catch (error) {
        if (window.showToast) {
            window.showToast('No se pudo iniciar PayPal. Intenta de nuevo.', 'error');
        }
        setPayPalModalMessage('Error al iniciar PayPal. Intenta nuevamente.', true);
    } finally {
        isProcessingCheckout = false;
        showCheckoutLoader(false);
    }
};

// ============================================
// POST-LOGIN PLAN CONTINUATION
// ============================================

window.handlePostAuthenticationPlanCheck = async function() {
    const selectedPlan = localStorage.getItem('selectedPlan');
    const userPlan = localStorage.getItem('userPlan') || 'free';

    if (selectedPlan && selectedPlan !== 'free' && userPlan === 'free') {
        const confirmed = await window.showConfirm({
            title: 'Continuar Suscripción',
            message: `¿Quieres continuar con la suscripción a ${selectedPlan.toUpperCase()}?`,
            confirmText: 'Sí, continuar',
            cancelText: 'No, más tarde',
            type: 'info'
        });

        if (confirmed) {
            localStorage.removeItem('selectedPlan');
            await new Promise(resolve => setTimeout(resolve, 100));
            initiateCheckout(selectedPlan);
        } else {
            localStorage.removeItem('selectedPlan');
            window.location.href = '/journal';
        }
    } else {
        localStorage.removeItem('selectedPlan');
        if (window.location.pathname !== '/journal') {
            window.location.href = '/journal';
        }
    }
};

// ============================================
// UI HELPERS
// ============================================

function showCheckoutLoader(show) {
    let loader = document.getElementById('checkout-loader');

    if (show && !loader) {
        loader = document.createElement('div');
        loader.id = 'checkout-loader';
        loader.style.cssText = `
            position: fixed;
            top: 0;
            left: 0;
            width: 100%;
            height: 100%;
            background: rgba(0, 0, 0, 0.7);
            display: flex;
            align-items: center;
            justify-content: center;
            z-index: 9999;
        `;
        loader.innerHTML = `
            <div style="text-align: center; color: white;">
                <div style="width: 60px; height: 60px; border: 4px solid rgba(255,255,255,0.3); border-top: 4px solid white; border-radius: 50%; animation: spin 1s linear infinite; margin: 0 auto 20px;"></div>
                <p style="font-size: 18px; margin: 0;">Conectando con PayPal...</p>
            </div>
            <style>
                @keyframes spin {
                    to { transform: rotate(360deg); }
                }
            </style>
        `;
        document.body.appendChild(loader);
    } else if (!show && loader) {
        loader.remove();
    }
}

// ============================================
// PAGE LOAD INITIALIZATION
// ============================================

function initializePayPal() {
    updatePlanButtons();
}

if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initializePayPal);
} else {
    initializePayPal();
}
